import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type MutableRefObject,
} from "react";
import {
  useMutation,
  useQuery,
  keepPreviousData,
  type QueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import apiClient from "@shared/services/apiClient";
import type { useAppDialog } from "@shared/context/AppDialogContext";
import type { AnnotationTypeCatalogEntry } from "../components/eegViewerDisplayHelpers";
import { resolveEEGViewerAnnotationDisplayColor } from "../components/eegViewerDisplayHelpers";
import type { WaveformAnnotationMarkerItem } from "../components/WaveformAnnotationMarkers";
import type { AnnotationCategoryFilter } from "../components/eegViewerChromeShared";
import type { RapidEventType } from "../utils/rapidEventValidation";
import {
  ANNOTATION_PANEL_TABS,
  DEFAULT_ANNOTATION_CATEGORY_FILTER,
  RAPID_EVENT_COLORS,
  RAPID_EVENT_LABELS,
  buildInterventionLabel,
  isAnnotationCategoryFilter,
  isTechnicalImportedAnnotationTypeCode,
  normalizeAnnotationTypeCode,
  readJsonFromSessionStorage,
  toRapidEventType,
  toUserFacingApiError,
  type AnnotationCategoryBucket,
  type AnnotationNoteComposerState,
  type AnnotationPanelTab,
  type Annotation,
  type InterventionEvent,
  type InterventionResponse,
  type SmartAnnotationSuggestion,
  type SmartAnnotationSuggestionDraft,
} from "../types/eegViewerAnnotationTypes";

/**
 * Annotation panel + waveform annotation CRUD state extracted from
 * EEGViewer to keep the main component focused on rendering. Owns the
 * annotation panel UI state (tab/filters/bulk selection/note composer),
 * the annotation + pending-review + intervention React Query fetches, all
 * annotation/intervention mutations, and the CRUD/bulk/smart-suggestion
 * handlers wired to the panel and to waveform annotation markers.
 *
 * Deliberately does NOT own `showAnnotationPanel` — that flag is also read
 * by deferred-chrome/topography-suspend logic before this hook can be
 * called (its visibility gating depends on values computed from it), so it
 * stays in EEGViewer and is not part of this hook's surface.
 */

const EMPTY_ANNOTATIONS: Annotation[] = [];

export interface UseEEGAnnotationPanelParams {
  studyId: string;
  currentRecordingId?: string | null;
  clinicalAnnotationPanelVisible: boolean;
  shouldFetchAnnotations: boolean;
  shouldFetchInterventions: boolean;
  activeHighSpeedPlayback: boolean;
  currentTime: number;
  annotationQueryReferenceTime: number;
  sideQueryReferenceTime: number;
  timeScaleSeconds: number;
  visibleMontageChannelDetails: Array<{ label: string }>;
  selectedTraceChannels: string[];
  getAnnotationChannels: () => string[];
  smartSuggestionByIdRef: MutableRefObject<
    Map<string, SmartAnnotationSuggestion>
  >;
  annotationAudienceSessionStorageKey: string;
  annotationPanelTabRefs: MutableRefObject<
    Record<AnnotationPanelTab, HTMLButtonElement | null>
  >;
  setRapidEventStatus: (status: string | null) => void;
  setRapidEventError: (error: string | null) => void;
  dialog: ReturnType<typeof useAppDialog>;
  queryClient: QueryClient;
}

export function useEEGAnnotationPanel(params: UseEEGAnnotationPanelParams) {
  const {
    studyId,
    currentRecordingId,
    clinicalAnnotationPanelVisible,
    shouldFetchAnnotations,
    shouldFetchInterventions,
    activeHighSpeedPlayback,
    currentTime,
    annotationQueryReferenceTime,
    sideQueryReferenceTime,
    timeScaleSeconds,
    visibleMontageChannelDetails,
    selectedTraceChannels,
    getAnnotationChannels,
    smartSuggestionByIdRef,
    annotationAudienceSessionStorageKey,
    annotationPanelTabRefs,
    setRapidEventStatus,
    setRapidEventError,
    dialog,
    queryClient,
  } = params;

  // ---- Panel UI state -----------------------------------------------
  const [annotationPanelTab, setAnnotationPanelTab] =
    useState<AnnotationPanelTab>("annotate");
  const annotationPanelTabBaseId = useId();
  const safeAnnotationPanelTabBaseId = annotationPanelTabBaseId.replace(
    /[^A-Za-z0-9_-]/g,
    "",
  );
  const annotationPanelTabIds = {
    annotate: {
      tab: `${safeAnnotationPanelTabBaseId}-annotation-tab`,
      panel: `${safeAnnotationPanelTabBaseId}-annotation-panel`,
    },
    review: {
      tab: `${safeAnnotationPanelTabBaseId}-review-tab`,
      panel: `${safeAnnotationPanelTabBaseId}-review-panel`,
    },
    channels: {
      tab: `${safeAnnotationPanelTabBaseId}-channels-tab`,
      panel: `${safeAnnotationPanelTabBaseId}-channels-panel`,
    },
    trend_event: {
      tab: `${safeAnnotationPanelTabBaseId}-trend-event-tab`,
      panel: `${safeAnnotationPanelTabBaseId}-trend-event-panel`,
    },
  } as const;

  const [annotationOverlayError, setAnnotationOverlayError] = useState<
    string | null
  >(null);
  const [smartSuggestionDrafts, setSmartSuggestionDrafts] = useState<
    Record<string, SmartAnnotationSuggestionDraft>
  >({});
  const [dismissedSmartSuggestionIds, setDismissedSmartSuggestionIds] =
    useState<string[]>([]);
  const [smartSuggestionStatus, setSmartSuggestionStatus] = useState<
    string | null
  >(null);
  const [smartSuggestionError, setSmartSuggestionError] = useState<
    string | null
  >(null);
  const [annotationCategoryFilter, setAnnotationCategoryFilter] =
    useState<AnnotationCategoryFilter>(() => {
      const persisted = readJsonFromSessionStorage<unknown>(
        annotationAudienceSessionStorageKey,
        DEFAULT_ANNOTATION_CATEGORY_FILTER,
      );
      return isAnnotationCategoryFilter(persisted)
        ? persisted
        : DEFAULT_ANNOTATION_CATEGORY_FILTER;
    });
  const [annotationReviewFilter, setAnnotationReviewFilter] = useState<
    "all" | "pending" | "reviewed"
  >("all");
  const [annotationReviewStatus, setAnnotationReviewStatus] = useState<
    string | null
  >(null);
  const [annotationReviewError, setAnnotationReviewError] = useState<
    string | null
  >(null);
  const [selectedAnnotationIds, setSelectedAnnotationIds] = useState<
    string[]
  >([]);
  const [annotationBulkStatus, setAnnotationBulkStatus] = useState<
    string | null
  >(null);
  const [annotationBulkError, setAnnotationBulkError] = useState<
    string | null
  >(null);
  const [bulkAnnotationTypeId, setBulkAnnotationTypeId] =
    useState<string>("");
  const [bulkChannelScopeMode, setBulkChannelScopeMode] = useState<
    "all" | "selected"
  >("selected");
  const [bulkAnnotationNoteAppend, setBulkAnnotationNoteAppend] =
    useState<string>("");
  const [annotationNoteComposer, setAnnotationNoteComposer] =
    useState<AnnotationNoteComposerState | null>(null);
  const [annotationNoteComposerValue, setAnnotationNoteComposerValue] =
    useState("");
  const [annotationNoteComposerError, setAnnotationNoteComposerError] =
    useState<string | null>(null);
  const [selectedInterventionId, setSelectedInterventionId] = useState("");
  const [responseObservedEffect, setResponseObservedEffect] = useState<
    "improved" | "worsened" | "no_change"
  >("no_change");
  const [responseNotes, setResponseNotes] = useState("");
  const [responseSeverityDelta, setResponseSeverityDelta] = useState("");
  const [responseWindowSeconds, setResponseWindowSeconds] = useState("300");
  const [interventionTimelineError, setInterventionTimelineError] =
    useState<string | null>(null);

  useEffect(() => {
    const persisted = readJsonFromSessionStorage<unknown>(
      annotationAudienceSessionStorageKey,
      DEFAULT_ANNOTATION_CATEGORY_FILTER,
    );
    setAnnotationCategoryFilter(
      isAnnotationCategoryFilter(persisted)
        ? persisted
        : DEFAULT_ANNOTATION_CATEGORY_FILTER,
    );
  }, [annotationAudienceSessionStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(
        annotationAudienceSessionStorageKey,
        JSON.stringify(annotationCategoryFilter),
      );
    } catch {
      // Ignore persistence failures in constrained storage contexts.
    }
  }, [annotationAudienceSessionStorageKey, annotationCategoryFilter]);

  const handleResetAnnotationCategoryFilter = useCallback(() => {
    setAnnotationCategoryFilter(DEFAULT_ANNOTATION_CATEGORY_FILTER);
  }, []);

  // ---- Queries --------------------------------------------------------
  const {
    data: annotationTypeCatalog = [],
    isError: annotationCatalogErrored,
  } = useQuery({
    queryKey: ["eeg-annotation-types", "viewer"],
    queryFn: async () => {
      const response = await apiClient.get("/eeg-annotation-types?for=viewer");
      const payload = response?.data;
      if (Array.isArray(payload)) {
        return payload as AnnotationTypeCatalogEntry[];
      }
      if (Array.isArray(payload?.data)) {
        return payload.data as AnnotationTypeCatalogEntry[];
      }
      return [] as AnnotationTypeCatalogEntry[];
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const annotationBucketSeconds = activeHighSpeedPlayback ? 240 : 30;
  const annotationTimeWindowKey = Math.floor(
    annotationQueryReferenceTime / annotationBucketSeconds,
  );
  const annotationQueryAnchor =
    annotationTimeWindowKey * annotationBucketSeconds;
  const annotationQueryStart = activeHighSpeedPlayback
    ? Math.max(0, annotationQueryAnchor - 30)
    : Math.max(0, currentTime - 30);
  const annotationQueryEnd = activeHighSpeedPlayback
    ? annotationQueryAnchor + annotationBucketSeconds + 30
    : currentTime + 30;

  const { data: annotations = EMPTY_ANNOTATIONS, refetch: refetchAnnotations } =
    useQuery({
      queryKey: [
        "eeg-annotations",
        studyId,
        currentRecordingId,
        annotationBucketSeconds,
        annotationTimeWindowKey,
      ],
      queryFn: async () => {
        const urlParams = new URLSearchParams({
          studyId,
          start: String(annotationQueryStart),
          end: String(annotationQueryEnd),
        });
        if (currentRecordingId) {
          urlParams.set("recordingId", currentRecordingId);
        }
        const response = await apiClient.get(
          `/eeg-annotations/time-range?${urlParams.toString()}`,
        );
        const payload = response?.data;
        if (Array.isArray(payload)) {
          return payload as Annotation[];
        }
        if (Array.isArray(payload?.data)) {
          return payload.data as Annotation[];
        }
        return EMPTY_ANNOTATIONS;
      },
      enabled: shouldFetchAnnotations,
      staleTime: activeHighSpeedPlayback ? 60_000 : 15_000,
      gcTime: activeHighSpeedPlayback ? 120_000 : 60_000,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
    });
  const activeAnnotationsQueryKey = useMemo(
    () => [
      "eeg-annotations",
      studyId,
      currentRecordingId,
      annotationBucketSeconds,
      annotationTimeWindowKey,
    ],
    [
      annotationBucketSeconds,
      annotationTimeWindowKey,
      currentRecordingId,
      studyId,
    ],
  );

  const {
    data: pendingReviewAnnotations = EMPTY_ANNOTATIONS,
    refetch: refetchPendingReviewAnnotations,
  } = useQuery({
    queryKey: ["eeg-annotations-pending-reviews", studyId, currentRecordingId],
    queryFn: async () => {
      const response = await apiClient.get("/eeg-annotations/pending-reviews");
      const payload = response?.data;
      const list: Annotation[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];

      return list.filter((annotation) => {
        if (!annotation || annotation.study_id !== studyId) return false;
        if (!currentRecordingId) return true;
        return annotation.recording_id === currentRecordingId;
      });
    },
    enabled: Boolean(studyId) && clinicalAnnotationPanelVisible,
    staleTime: 15_000,
    initialData: EMPTY_ANNOTATIONS,
    refetchOnWindowFocus: false,
  });

  const interventionWindowSeconds = Math.max(120, (timeScaleSeconds || 10) * 6);
  const interventionWindowStart = Math.max(
    0,
    currentTime - interventionWindowSeconds,
  );
  const interventionWindowEnd = currentTime + interventionWindowSeconds;
  const interventionBucketSize = activeHighSpeedPlayback
    ? Math.max(300, interventionWindowSeconds)
    : Math.max(60, interventionWindowSeconds);
  const interventionWindowKey = Math.floor(
    sideQueryReferenceTime / interventionBucketSize,
  );
  const interventionQueryAnchor =
    interventionWindowKey * interventionBucketSize;
  const interventionQueryStart = activeHighSpeedPlayback
    ? Math.max(0, interventionQueryAnchor - interventionWindowSeconds)
    : interventionWindowStart;
  const interventionQueryEnd = activeHighSpeedPlayback
    ? interventionQueryAnchor +
      interventionBucketSize +
      interventionWindowSeconds
    : interventionWindowEnd;

  const {
    data: interventionEvents = [],
    refetch: refetchInterventions,
    isFetching: isFetchingInterventions,
    error: interventionsError,
  } = useQuery({
    queryKey: [
      "eeg-interventions",
      studyId,
      interventionWindowKey,
      interventionWindowSeconds,
    ],
    queryFn: async () => {
      const response = await apiClient.get("/eeg/interventions/time-range", {
        params: {
          studyId,
          startTimeSeconds: interventionQueryStart,
          endTimeSeconds: interventionQueryEnd,
        },
      });
      const payload = response?.data?.data;
      return Array.isArray(payload) ? (payload as InterventionEvent[]) : [];
    },
    enabled: shouldFetchInterventions,
    staleTime: activeHighSpeedPlayback ? 60_000 : 30_000,
    gcTime: activeHighSpeedPlayback ? 120_000 : 60_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  const {
    data: selectedInterventionTimeline = null,
    refetch: refetchInterventionTimeline,
    isFetching: isFetchingInterventionTimeline,
  } = useQuery({
    queryKey: ["eeg-intervention-timeline", selectedInterventionId],
    queryFn: async () => {
      if (!selectedInterventionId) return null;
      const response = await apiClient.get(
        `/eeg/interventions/${selectedInterventionId}/timeline`,
      );
      return response?.data?.data as {
        intervention: InterventionEvent;
        timeline: Array<{
          kind: "intervention" | "response";
          time_seconds: number;
          observed_effect?: string | null;
          severity_delta?: number | null;
        }>;
      } | null;
    },
    enabled: Boolean(selectedInterventionId),
    staleTime: 5000,
    refetchOnWindowFocus: false,
  });

  const interventionAnnotations = useMemo<Annotation[]>(() => {
    return interventionEvents.map((event) => {
      const rapidEventType = toRapidEventType(event.event_type);
      const color = rapidEventType
        ? RAPID_EVENT_COLORS[rapidEventType]
        : "#8b5cf6";
      return {
        annotation_id: event.intervention_id,
        annotation_type: `intervention_${event.event_type}`,
        label: buildInterventionLabel(event),
        start_time: event.event_time_seconds,
        end_time: event.event_time_seconds + 0.5,
        channels_affected: ["all"],
        color,
      };
    });
  }, [interventionEvents]);

  const annotationTypeByCode = useMemo(() => {
    return new Map(
      annotationTypeCatalog.map((entry) => [
        normalizeAnnotationTypeCode(entry.type_code),
        entry,
      ]),
    );
  }, [annotationTypeCatalog]);

  const resolveAnnotationCategory = useCallback(
    (
      annotation: Pick<Annotation, "annotation_type">,
    ): AnnotationCategoryBucket => {
      const typeCode = normalizeAnnotationTypeCode(annotation.annotation_type);
      if (isTechnicalImportedAnnotationTypeCode(typeCode)) {
        return "technical_imported";
      }

      const normalizedCatalogCategory = (
        annotationTypeByCode.get(typeCode)?.category || ""
      )
        .trim()
        .toLowerCase();

      if (
        normalizedCatalogCategory.includes("technical") ||
        normalizedCatalogCategory.includes("import")
      ) {
        return "technical_imported";
      }

      return "clinical";
    },
    [annotationTypeByCode],
  );

  const resolveAnnotationTypeCategory = useCallback(
    (
      type: Pick<AnnotationTypeCatalogEntry, "type_code" | "category">,
    ): AnnotationCategoryBucket => {
      const typeCode = normalizeAnnotationTypeCode(type.type_code);
      if (
        isTechnicalImportedAnnotationTypeCode(typeCode) ||
        typeCode.includes("artifact")
      ) {
        return "technical_imported";
      }

      const normalizedCategory = (type.category || "").trim().toLowerCase();
      if (
        normalizedCategory.includes("technical") ||
        normalizedCategory.includes("import") ||
        normalizedCategory.includes("artifact")
      ) {
        return "technical_imported";
      }

      return "clinical";
    },
    [],
  );

  const resolveAnnotationDisplayColor = useCallback(
    (
      annotation: Pick<Annotation, "annotation_type" | "color">,
      fallbackColor?: string | null,
    ) => {
      const typeCode = normalizeAnnotationTypeCode(annotation.annotation_type);
      const catalogColor = annotationTypeByCode.get(typeCode)?.color || null;
      return resolveEEGViewerAnnotationDisplayColor({
        annotationType: annotation.annotation_type,
        annotationColor: annotation.color,
        catalogColor,
        fallbackColor,
        isTechnicalImported:
          resolveAnnotationCategory(annotation) === "technical_imported",
      });
    },
    [annotationTypeByCode, resolveAnnotationCategory],
  );

  const categoryFilteredAnnotations = useMemo(() => {
    const source = annotations || EMPTY_ANNOTATIONS;
    if (annotationCategoryFilter === "all") {
      return source;
    }

    return source.filter(
      (annotation) =>
        resolveAnnotationCategory(annotation) === annotationCategoryFilter,
    );
  }, [annotationCategoryFilter, annotations, resolveAnnotationCategory]);

  const categoryFilteredPendingReviewAnnotations = useMemo(() => {
    const source = pendingReviewAnnotations || EMPTY_ANNOTATIONS;
    if (annotationCategoryFilter === "all") {
      return source;
    }

    return source.filter(
      (annotation) =>
        resolveAnnotationCategory(annotation) === annotationCategoryFilter,
    );
  }, [
    annotationCategoryFilter,
    pendingReviewAnnotations,
    resolveAnnotationCategory,
  ]);

  const renderAnnotations = useMemo<Annotation[]>(() => {
    const linkedLegacyIds = new Set(
      interventionEvents
        .map((event) => event.linked_annotation_id)
        .filter(
          (value): value is string =>
            typeof value === "string" && value.length > 0,
        ),
    );

    const baseAnnotations = categoryFilteredAnnotations.filter(
      (annotation) =>
        !annotation.annotation_id ||
        !linkedLegacyIds.has(annotation.annotation_id),
    );
    const visibleInterventionAnnotations =
      annotationCategoryFilter === "technical_imported"
        ? EMPTY_ANNOTATIONS
        : interventionAnnotations;

    return [...baseAnnotations, ...visibleInterventionAnnotations];
  }, [
    annotationCategoryFilter,
    categoryFilteredAnnotations,
    interventionAnnotations,
    interventionEvents,
  ]);

  const persistedAnnotationById = useMemo(() => {
    const map = new Map<string, Annotation>();
    (annotations || EMPTY_ANNOTATIONS).forEach((annotation) => {
      if (!annotation.annotation_id) return;
      map.set(annotation.annotation_id, annotation);
    });
    return map;
  }, [annotations]);

  const pendingReviewIdSet = useMemo(() => {
    const set = new Set<string>();
    categoryFilteredPendingReviewAnnotations.forEach((annotation) => {
      if (!annotation.annotation_id) return;
      set.add(annotation.annotation_id);
    });
    return set;
  }, [categoryFilteredPendingReviewAnnotations]);

  const reviewFilteredAnnotations = useMemo(() => {
    const source = categoryFilteredAnnotations;
    const filtered = source.filter((annotation) => {
      const isReviewed = Boolean(annotation.is_reviewed);
      if (annotationReviewFilter === "reviewed") return isReviewed;
      if (annotationReviewFilter === "pending") {
        if (!annotation.annotation_id) return !isReviewed;
        return !isReviewed || pendingReviewIdSet.has(annotation.annotation_id);
      }
      return true;
    });
    return [...filtered].sort((a, b) => a.start_time - b.start_time);
  }, [annotationReviewFilter, categoryFilteredAnnotations, pendingReviewIdSet]);

  const annotationAudienceStatusChip = useMemo(() => {
    if (annotationCategoryFilter === "clinical") {
      return {
        label: "Technical hidden",
        title:
          "Technical/imported annotations are hidden. Click to restore all annotation types.",
      };
    }

    if (annotationCategoryFilter === "technical_imported") {
      return {
        label: "Clinical hidden",
        title:
          "Clinical annotations are hidden. Click to restore all annotation types.",
      };
    }

    return null;
  }, [annotationCategoryFilter]);

  const pendingReviewCount = useMemo(() => {
    const countFromWindow = categoryFilteredAnnotations.filter(
      (annotation) => !annotation.is_reviewed,
    ).length;
    return Math.max(countFromWindow, pendingReviewIdSet.size);
  }, [categoryFilteredAnnotations, pendingReviewIdSet]);

  const reviewFilteredAnnotationIds = useMemo(
    () =>
      reviewFilteredAnnotations
        .map((annotation) => annotation.annotation_id)
        .filter((id): id is string => typeof id === "string" && id.length > 0),
    [reviewFilteredAnnotations],
  );

  const selectedAnnotationIdSet = useMemo(
    () => new Set(selectedAnnotationIds),
    [selectedAnnotationIds],
  );

  const selectedAnnotations = useMemo(
    () =>
      (annotations || EMPTY_ANNOTATIONS).filter(
        (annotation) =>
          !!annotation.annotation_id &&
          selectedAnnotationIdSet.has(annotation.annotation_id),
      ),
    [annotations, selectedAnnotationIdSet],
  );

  const selectedClipSegmentAnnotation = useMemo(() => {
    if (selectedAnnotations.length !== 1) return null;
    const annotation = selectedAnnotations[0];
    const typeCode = normalizeAnnotationTypeCode(annotation.annotation_type);
    if (typeCode !== "clip_segment") return null;
    if (!annotation.annotation_id) return null;
    if (currentRecordingId && annotation.recording_id !== currentRecordingId) {
      return null;
    }
    const startTime = Number(annotation.start_time);
    const endTime = Number(annotation.end_time);
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return null;
    if (endTime <= startTime) return null;
    return annotation;
  }, [currentRecordingId, selectedAnnotations]);

  const clipScissorsDisabledReason = useMemo(() => {
    if (selectedAnnotations.length !== 1) {
      return "Select one Clip Segment duration annotation";
    }
    const annotation = selectedAnnotations[0];
    if (normalizeAnnotationTypeCode(annotation.annotation_type) !== "clip_segment") {
      return "Scissors only works with Clip Segment annotations";
    }
    if (!annotation.annotation_id) {
      return "Save the Clip Segment annotation before clipping";
    }
    if (currentRecordingId && annotation.recording_id !== currentRecordingId) {
      return "Clip Segment must belong to the active recording";
    }
    const startTime = Number(annotation.start_time);
    const endTime = Number(annotation.end_time);
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
      return "Clip Segment must have a positive duration";
    }
    return null;
  }, [currentRecordingId, selectedAnnotations]);

  const selectedPendingAnnotationIds = useMemo(
    () =>
      selectedAnnotations
        .filter(
          (annotation) => !annotation.is_reviewed && !!annotation.annotation_id,
        )
        .map((annotation) => annotation.annotation_id as string),
    [selectedAnnotations],
  );

  const allVisibleAnnotationsSelected =
    reviewFilteredAnnotationIds.length > 0 &&
    reviewFilteredAnnotationIds.every((id) => selectedAnnotationIdSet.has(id));

  useEffect(() => {
    const visibleIdSet = new Set(reviewFilteredAnnotationIds);
    setSelectedAnnotationIds((current) =>
      current.filter((id) => visibleIdSet.has(id)),
    );
  }, [reviewFilteredAnnotationIds]);

  const waveformAnnotationMarkers = useMemo<WaveformAnnotationMarkerItem[]>(
    () => {
      const visibleLabels = visibleMontageChannelDetails.map((channel) =>
        channel.label.toLowerCase(),
      );
      return renderAnnotations.map((annotation, index) => {
        const annotationType =
          annotation.annotation_type?.toLowerCase?.() || "annotation";
        const typeMeta = annotationTypeByCode.get(annotationType);
        const persisted =
          annotation.annotation_id &&
          persistedAnnotationById.has(annotation.annotation_id)
            ? persistedAnnotationById.get(annotation.annotation_id) || null
            : null;
        const supportsDuration =
          Boolean(typeMeta?.supports_duration) || Boolean(annotation.end_time);
        const defaultDraggable = true;
        const defaultResizable = supportsDuration;
        const hasPersistedDuration = Boolean(annotation.end_time);
        const draggable = hasPersistedDuration
          ? true
          : (typeMeta?.supports_drag_move ?? defaultDraggable);
        const resizable = hasPersistedDuration
          ? true
          : (typeMeta?.supports_resize ?? defaultResizable);
        const noteEditable = typeMeta?.supports_notes ?? true;
        const minDurationSeconds =
          typeof typeMeta?.min_duration_seconds === "number" &&
          Number.isFinite(typeMeta.min_duration_seconds)
            ? Math.max(0.05, typeMeta.min_duration_seconds)
            : 0.1;
        const maxDurationSeconds =
          typeof typeMeta?.max_duration_seconds === "number" &&
          Number.isFinite(typeMeta.max_duration_seconds) &&
          typeMeta.max_duration_seconds > minDurationSeconds
            ? typeMeta.max_duration_seconds
            : null;
        const channelsAffected = Array.isArray(annotation.channels_affected)
          ? annotation.channels_affected.filter(Boolean)
          : [];
        const hasScopedChannels = channelsAffected.some(
          (channel) => channel.toLowerCase() !== "all",
        );
        const channelScope =
          annotation.channel_scope === "channel_set" || hasScopedChannels
            ? "channel_set"
            : "all";
        const firstAffectedChannel =
          channelScope === "channel_set"
            ? channelsAffected.find((channel) => channel && channel !== "all")
            : null;
        const laneIndex =
          firstAffectedChannel && visibleLabels.length > 0
            ? visibleLabels.findIndex((label) => {
                const normalized = firstAffectedChannel.toLowerCase();
                return (
                  label === normalized ||
                  label.includes(normalized) ||
                  normalized.includes(label)
                );
              })
            : -1;
        const laneTopPercent =
          laneIndex >= 0 && visibleLabels.length > 0
            ? ((laneIndex + 0.5) / visibleLabels.length) * 100
            : null;

        return {
          id:
            annotation.annotation_id ||
            `${annotationType}-${annotation.start_time}-${index}`,
          annotationId: persisted?.annotation_id || null,
          typeCode: annotationType,
          label: annotation.label || annotationType,
          notes: annotation.notes,
          startTime: annotation.start_time,
          endTime: annotation.end_time,
          color: resolveAnnotationDisplayColor(annotation, typeMeta?.color),
          editable: Boolean(persisted?.annotation_id),
          draggable,
          resizable,
          noteEditable,
          reviewed: Boolean(annotation.is_reviewed),
          minDurationSeconds,
          maxDurationSeconds,
          channelScope,
          channelsAffected,
          laneTopPercent,
        };
      });
    },
    [
      annotationTypeByCode,
      persistedAnnotationById,
      renderAnnotations,
      resolveAnnotationDisplayColor,
      visibleMontageChannelDetails,
    ],
  );

  // ---- Mutations --------------------------------------------------------
  const createAnnotationMutation = useMutation({
    mutationFn: async (
      annotation: Omit<Annotation, "annotation_id"> & {
        template_usage?: {
          template_id?: string | null;
          template_name?: string | null;
          template_category?: string | null;
          required_fields?: string[];
          structured_fields?: Record<string, unknown>;
        };
        suggestion_decision?: {
          suggestion_id?: string | null;
          source?: string | null;
          confidence?: number | null;
          explanation?: string | null;
          edited?: boolean;
          proposed_annotation?: Record<string, unknown>;
        };
      },
    ) => {
      const channels = Array.isArray(annotation.channels_affected)
        ? annotation.channels_affected
        : ["all"];
      const normalizedChannelScope =
        annotation.channel_scope ||
        (channels.length === 0 || channels.includes("all")
          ? "all"
          : "channel_set");

      return await apiClient.post("/eeg-annotations", {
        ...annotation,
        study_id: studyId,
        recording_id: annotation.recording_id ?? currentRecordingId ?? null,
        channel_scope: normalizedChannelScope,
        channels_affected: channels.length > 0 ? channels : ["all"],
      });
    },
    onMutate: async (annotation) => {
      // Optimistic: insert annotation into local cache immediately so it
      // appears on the waveform without waiting for the API roundtrip.
      // This prevents users from double-clicking when they see no feedback.
      const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const optimisticAnnotation: Annotation = {
        annotation_id: optimisticId,
        study_id: studyId,
        recording_id: annotation.recording_id ?? currentRecordingId ?? null,
        annotation_type: annotation.annotation_type,
        annotation_type_id: annotation.annotation_type_id ?? null,
        label: annotation.label,
        description: annotation.description ?? null,
        notes: annotation.notes ?? null,
        start_time: annotation.start_time,
        end_time: annotation.end_time ?? undefined,
        channel_scope: annotation.channel_scope ?? "all",
        channels_affected: annotation.channels_affected ?? ["all"],
        color: annotation.color ?? undefined,
      };

      const annotationsQueryKey = [
        "eeg-annotations",
        studyId,
        currentRecordingId,
        annotationBucketSeconds,
        annotationTimeWindowKey,
      ];
      await queryClient.cancelQueries({ queryKey: annotationsQueryKey });
      const previous =
        queryClient.getQueryData<Annotation[]>(annotationsQueryKey);
      if (previous) {
        queryClient.setQueryData<Annotation[]>(annotationsQueryKey, [
          ...previous,
          optimisticAnnotation,
        ]);
      }
      return { previous, annotationsQueryKey, optimisticId };
    },
    onSuccess: (response, _annotation, context) => {
      // Replace the optimistic annotation with the server response
      if (context?.annotationsQueryKey) {
        const serverAnnotation: Annotation | undefined =
          response?.data ?? response;
        if (serverAnnotation?.annotation_id && context.optimisticId) {
          queryClient.setQueryData<Annotation[]>(
            context.annotationsQueryKey,
            (old) =>
              old?.map((a) =>
                a.annotation_id === context.optimisticId ? serverAnnotation : a,
              ) ?? [],
          );
        } else {
          // Server response didn't include the created annotation; do a full refetch
          refetchAnnotations();
        }
      } else {
        refetchAnnotations();
      }
    },
    onError: (_error, _annotation, context) => {
      // Rollback to the previous state
      if (context?.previous && context.annotationsQueryKey) {
        queryClient.setQueryData<Annotation[]>(
          context.annotationsQueryKey,
          context.previous,
        );
      }
    },
  });

  const recordSuggestionDecisionMutation = useMutation({
    mutationFn: async (payload: {
      study_id: string;
      recording_id?: string | null;
      suggestion_id: string;
      action: "accepted" | "rejected";
      source: string;
      confidence: number;
      explanation: string;
      proposed_annotation: Record<string, unknown>;
      reason?: string;
    }) => {
      const response = await apiClient.post(
        "/eeg-annotations/suggestions/decision",
        payload,
      );
      return response?.data?.data ?? response?.data;
    },
  });

  const buildAnnotationUpdatePayload = useCallback(
    (
      base: Annotation,
      overrides: Partial<
        Pick<
          Annotation,
          "start_time" | "end_time" | "notes" | "channel_scope" | "channels_affected"
        >
      >,
    ) => {
      const channels = Array.isArray(base.channels_affected)
        ? base.channels_affected.filter(Boolean)
        : [];
      const overrideChannels = Array.isArray(overrides.channels_affected)
        ? overrides.channels_affected.filter(Boolean)
        : null;
      const normalizedChannels =
        overrideChannels !== null
          ? overrideChannels.length > 0
            ? overrideChannels
            : ["all"]
          : channels.length > 0
            ? channels
            : ["all"];
      const channelScope =
        overrides.channel_scope ||
        base.channel_scope ||
        (normalizedChannels.length === 0 || normalizedChannels.includes("all")
          ? "all"
          : "channel_set");

      return {
        study_id: base.study_id || studyId,
        recording_id: base.recording_id ?? currentRecordingId ?? null,
        annotation_type: base.annotation_type,
        annotation_type_id: base.annotation_type_id ?? null,
        label: base.label,
        description: base.description ?? null,
        notes:
          overrides.notes !== undefined
            ? (overrides.notes ?? null)
            : (base.notes ?? null),
        start_time:
          typeof overrides.start_time === "number"
            ? overrides.start_time
            : base.start_time,
        end_time:
          overrides.end_time !== undefined
            ? (overrides.end_time ?? null)
            : (base.end_time ?? null),
        channel_scope: channelScope,
        channels_affected: normalizedChannels,
        color: base.color ?? null,
      };
    },
    [currentRecordingId, studyId],
  );

  const updateAnnotationMutation = useMutation({
    mutationFn: async (payload: {
      annotationId: string;
      updates: Partial<
        Pick<
          Annotation,
          "start_time" | "end_time" | "notes" | "channel_scope" | "channels_affected"
        >
      >;
    }) => {
      const current = persistedAnnotationById.get(payload.annotationId);
      if (!current) {
        throw new Error("Annotation no longer available for editing.");
      }
      return await apiClient.put(
        `/eeg-annotations/${payload.annotationId}`,
        buildAnnotationUpdatePayload(current, payload.updates),
      );
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: activeAnnotationsQueryKey });
      const previous =
        queryClient.getQueryData<Annotation[]>(activeAnnotationsQueryKey);
      queryClient.setQueryData<Annotation[]>(
        activeAnnotationsQueryKey,
        (old) =>
          old?.map((annotation) =>
            annotation.annotation_id === payload.annotationId
              ? {
                  ...annotation,
                  ...payload.updates,
                  channel_scope:
                    payload.updates.channel_scope ?? annotation.channel_scope,
                  channels_affected:
                    payload.updates.channels_affected ??
                    annotation.channels_affected,
                }
              : annotation,
          ) ?? old,
      );
      return { previous };
    },
    onSuccess: async (response, payload) => {
      setAnnotationOverlayError(null);
      const updatedAnnotation: Annotation | undefined =
        response?.data?.data ?? response?.data ?? response;
      if (updatedAnnotation?.annotation_id) {
        queryClient.setQueryData<Annotation[]>(
          activeAnnotationsQueryKey,
          (old) =>
            old?.map((annotation) =>
              annotation.annotation_id === payload.annotationId
                ? updatedAnnotation
                : annotation,
            ) ?? old,
        );
      }
      await refetchAnnotations();
    },
    onError: (error, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData<Annotation[]>(
          activeAnnotationsQueryKey,
          context.previous,
        );
      }
      const normalized = toUserFacingApiError(error);
      setAnnotationOverlayError(normalized.message);
    },
  });

  const createInterventionMutation = useMutation({
    mutationFn: async (payload: {
      event_type: RapidEventType;
      event_time_seconds: number;
      medication_name?: string;
      medication_dose?: string;
      medication_route?: string;
      procedure_name?: string;
      reason?: string;
      expected_effect?: string;
      legacy_annotation_label?: string;
      follow_up_window_seconds?: number;
      selected_channels?: string[];
      legacy_annotation_channels?: string[];
    }) => {
      const response = await apiClient.post("/eeg/interventions", {
        study_id: studyId,
        recording_id: currentRecordingId || null,
        event_time_seconds: payload.event_time_seconds,
        event_type: payload.event_type,
        medication_name: payload.medication_name,
        medication_dose: payload.medication_dose,
        medication_route: payload.medication_route,
        procedure_name: payload.procedure_name,
        reason: payload.reason,
        expected_effect: payload.expected_effect,
        payload_json: {
          selected_channels: payload.selected_channels || ["all"],
          follow_up_window_seconds: payload.follow_up_window_seconds ?? null,
        },
        source: "rapid_action_panel",
        create_legacy_annotation: true,
        legacy_annotation_type: payload.event_type,
        legacy_annotation_label:
          payload.legacy_annotation_label ||
          RAPID_EVENT_LABELS[payload.event_type],
        legacy_annotation_description:
          payload.reason || payload.expected_effect,
        legacy_annotation_channels: payload.legacy_annotation_channels ||
          payload.selected_channels || ["all"],
        legacy_annotation_color: RAPID_EVENT_COLORS[payload.event_type],
      });
      return response?.data?.data as InterventionEvent;
    },
    onSuccess: async (event) => {
      setRapidEventError(null);
      setRapidEventStatus(
        `Recorded ${buildInterventionLabel(event)} at ${event.event_time_seconds.toFixed(1)}s.`,
      );
      setSelectedInterventionId(event.intervention_id);
      await refetchInterventions();
      await queryClient.invalidateQueries({
        queryKey: ["eeg-intervention-timeline", event.intervention_id],
      });
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setRapidEventStatus(null);
      setRapidEventError(normalized.message);
    },
  });

  const createInterventionResponseMutation = useMutation({
    mutationFn: async (payload: {
      interventionId: string;
      observed_effect: "improved" | "worsened" | "no_change";
      notes?: string;
      severity_delta?: number;
      response_window_seconds?: number;
    }) => {
      const response = await apiClient.post(
        `/eeg/interventions/${payload.interventionId}/response`,
        {
          observed_at_seconds: currentTime,
          observed_effect: payload.observed_effect,
          notes: payload.notes,
          severity_delta: payload.severity_delta,
          response_window_seconds: payload.response_window_seconds,
        },
      );
      return response?.data?.data as InterventionResponse;
    },
    onSuccess: async () => {
      setInterventionTimelineError(null);
      setRapidEventError(null);
      setRapidEventStatus(
        `Response captured at ${currentTime.toFixed(1)}s (${responseObservedEffect.replace("_", " ")}).`,
      );
      setResponseNotes("");
      setResponseSeverityDelta("");
      await refetchInterventions();
      await refetchInterventionTimeline();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setRapidEventStatus(null);
      setInterventionTimelineError(normalized.message);
    },
  });

  const submitInterventionResponse = useCallback(() => {
    if (!selectedInterventionId) {
      setInterventionTimelineError("Select an intervention event first.");
      return;
    }
    const parsedSeverityDelta = Number(responseSeverityDelta);
    const parsedResponseWindow = Number(responseWindowSeconds);
    createInterventionResponseMutation.mutate({
      interventionId: selectedInterventionId,
      observed_effect: responseObservedEffect,
      notes: responseNotes || undefined,
      severity_delta: Number.isFinite(parsedSeverityDelta)
        ? parsedSeverityDelta
        : undefined,
      response_window_seconds: Number.isFinite(parsedResponseWindow)
        ? Math.max(0, Math.floor(parsedResponseWindow))
        : undefined,
    });
  }, [
    createInterventionResponseMutation,
    responseNotes,
    responseObservedEffect,
    responseSeverityDelta,
    responseWindowSeconds,
    selectedInterventionId,
  ]);

  const deleteAnnotationMutation = useMutation({
    mutationFn: async (annotationId: string) => {
      return await apiClient.delete(`/eeg-annotations/${annotationId}`);
    },
    onSuccess: () => {
      setAnnotationOverlayError(null);
      refetchAnnotations();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setAnnotationOverlayError(normalized.message);
    },
  });

  const reviewAnnotationMutation = useMutation({
    mutationFn: async (annotationId: string) => {
      return await apiClient.post(
        `/eeg-annotations/${annotationId}/review`,
        {},
      );
    },
    onSuccess: async () => {
      setAnnotationReviewError(null);
      setAnnotationReviewStatus("Annotation marked as reviewed.");
      await refetchAnnotations();
      await refetchPendingReviewAnnotations();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setAnnotationReviewStatus(null);
      setAnnotationReviewError(normalized.message);
    },
  });

  const bulkReviewAnnotationsMutation = useMutation({
    mutationFn: async (annotationIds: string[]) => {
      const response = await apiClient.post("/eeg-annotations/review-bulk", {
        annotationIds,
      });
      return response?.data as
        | {
            total: number;
            reviewed: number;
            failed: number;
          }
        | undefined;
    },
    onSuccess: async (result) => {
      const reviewed = Number(result?.reviewed || 0);
      const failed = Number(result?.failed || 0);
      setAnnotationBulkError(null);
      setAnnotationBulkStatus(
        `Bulk review complete: ${reviewed} reviewed, ${failed} failed.`,
      );
      setSelectedAnnotationIds([]);
      await refetchAnnotations();
      await refetchPendingReviewAnnotations();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(normalized.message);
    },
  });

  const bulkDeleteAnnotationsMutation = useMutation({
    mutationFn: async (annotationIds: string[]) => {
      const response = await apiClient.post("/eeg-annotations/delete-bulk", {
        annotationIds,
      });
      return response?.data as
        | {
            total: number;
            deleted: number;
            failed: number;
          }
        | undefined;
    },
    onSuccess: async (result) => {
      const deleted = Number(result?.deleted || 0);
      const failed = Number(result?.failed || 0);
      setAnnotationBulkError(null);
      setAnnotationBulkStatus(
        `Bulk delete complete: ${deleted} deleted, ${failed} failed.`,
      );
      setSelectedAnnotationIds([]);
      await refetchAnnotations();
      await refetchPendingReviewAnnotations();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(normalized.message);
    },
  });

  const bulkUpdateAnnotationsMutation = useMutation({
    mutationFn: async (payload: {
      annotationIds: string[];
      updates: {
        annotation_type?: string;
        annotation_type_id?: string;
        channel_scope?: "all" | "channel_set";
        channels_affected?: string[];
        note_append?: string;
      };
    }) => {
      const response = await apiClient.post(
        "/eeg-annotations/update-bulk",
        payload,
      );
      return response?.data as
        | {
            total: number;
            updated: number;
            failed: number;
          }
        | undefined;
    },
    onSuccess: async (result) => {
      const updated = Number(result?.updated || 0);
      const failed = Number(result?.failed || 0);
      setAnnotationBulkError(null);
      setAnnotationBulkStatus(
        `Bulk update complete: ${updated} updated, ${failed} failed.`,
      );
      await refetchAnnotations();
      await refetchPendingReviewAnnotations();
    },
    onError: (error) => {
      const normalized = toUserFacingApiError(error);
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(normalized.message);
    },
  });

  const annotationBulkActionPending =
    bulkReviewAnnotationsMutation.isPending ||
    bulkDeleteAnnotationsMutation.isPending ||
    bulkUpdateAnnotationsMutation.isPending;

  // ---- Note composer handlers ------------------------------------------
  const openAnnotationNoteComposer = useCallback(
    (state: AnnotationNoteComposerState) => {
      setAnnotationNoteComposer(state);
      setAnnotationNoteComposerValue(state.defaultValue || "");
      setAnnotationNoteComposerError(null);
    },
    [],
  );

  const closeAnnotationNoteComposer = useCallback(
    (invokeCancel: boolean) => {
      const current = annotationNoteComposer;
      setAnnotationNoteComposer(null);
      setAnnotationNoteComposerValue("");
      setAnnotationNoteComposerError(null);
      if (invokeCancel) {
        current?.onCancel?.();
      }
    },
    [annotationNoteComposer],
  );

  const submitAnnotationNoteComposer = useCallback(async () => {
    if (!annotationNoteComposer) return;
    const trimmed = annotationNoteComposerValue.trim();
    if (annotationNoteComposer.requireText && !trimmed) {
      setAnnotationNoteComposerError("A short note is required.");
      return;
    }
    try {
      const confirmed = await dialog.confirm({
        title: "Save annotation change?",
        message: "Save this note change to the annotation?",
        confirmText: "Save change",
        cancelText: "Cancel",
      });
      if (!confirmed) return;
      await annotationNoteComposer.onSubmit(trimmed);
      setAnnotationNoteComposer(null);
      setAnnotationNoteComposerValue("");
      setAnnotationNoteComposerError(null);
    } catch (error) {
      setAnnotationNoteComposerError(toUserFacingApiError(error).message);
    }
  }, [annotationNoteComposer, annotationNoteComposerValue, dialog]);

  // ---- Tab keyboard navigation -----------------------------------------
  const handleAnnotationPanelTabKeyDown = useCallback(
    (
      event: React.KeyboardEvent<HTMLButtonElement>,
      currentTab: AnnotationPanelTab,
    ) => {
      const currentIndex = ANNOTATION_PANEL_TABS.indexOf(currentTab);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      switch (event.key) {
        case "ArrowRight":
          nextIndex = (currentIndex + 1) % ANNOTATION_PANEL_TABS.length;
          break;
        case "ArrowLeft":
          nextIndex =
            (currentIndex - 1 + ANNOTATION_PANEL_TABS.length) %
            ANNOTATION_PANEL_TABS.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = ANNOTATION_PANEL_TABS.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();

      const nextTab = ANNOTATION_PANEL_TABS[nextIndex];
      setAnnotationPanelTab(nextTab);
      annotationPanelTabRefs.current[nextTab]?.focus();
    },
    [annotationPanelTabRefs],
  );

  // ---- Smart suggestion accept / dismiss -------------------------------
  const updateSmartSuggestionDraft = useCallback(
    (suggestionId: string, patch: Partial<SmartAnnotationSuggestionDraft>) => {
      setSmartSuggestionDrafts((current) => ({
        ...current,
        [suggestionId]: {
          ...(current[suggestionId] || {
            label: "",
            startTime: "",
            durationSeconds: "",
            notes: "",
          }),
          ...patch,
        },
      }));
      setSmartSuggestionError(null);
      setSmartSuggestionStatus(null);
    },
    [],
  );

  const dismissSmartSuggestion = useCallback((suggestionId: string) => {
    setDismissedSmartSuggestionIds((current) =>
      current.includes(suggestionId) ? current : [...current, suggestionId],
    );
  }, []);

  const rejectSmartSuggestion = useCallback(
    (suggestionId: string) => {
      const suggestion = smartSuggestionByIdRef.current.get(suggestionId);
      if (!suggestion) return;
      const draft = smartSuggestionDrafts[suggestionId];
      setSmartSuggestionError(null);
      setSmartSuggestionStatus(null);

      recordSuggestionDecisionMutation.mutate(
        {
          study_id: studyId,
          recording_id: currentRecordingId ?? null,
          suggestion_id: suggestion.id,
          action: "rejected",
          source: suggestion.source,
          confidence: suggestion.confidence,
          explanation: suggestion.explanation,
          reason: "Clinician rejected suggested annotation.",
          proposed_annotation: {
            annotation_type: suggestion.annotationType,
            label: draft?.label || suggestion.label,
            start_time:
              draft?.startTime && Number.isFinite(Number(draft.startTime))
                ? Number(draft.startTime)
                : suggestion.startTime,
            duration_seconds:
              draft?.durationSeconds &&
              Number.isFinite(Number(draft.durationSeconds))
                ? Number(draft.durationSeconds)
                : suggestion.defaultDurationSeconds,
            notes: (draft?.notes || "").trim() || null,
            channels_affected:
              suggestion.channels.length > 0 ? suggestion.channels : ["all"],
            marker_type: suggestion.markerType,
            metric: suggestion.metric,
          },
        },
        {
          onSuccess: () => {
            dismissSmartSuggestion(suggestion.id);
            setSmartSuggestionStatus(
              `Rejected suggestion at ${suggestion.startTime.toFixed(1)}s.`,
            );
          },
          onError: (error) => {
            setSmartSuggestionError(toUserFacingApiError(error).message);
          },
        },
      );
    },
    [
      currentRecordingId,
      dismissSmartSuggestion,
      recordSuggestionDecisionMutation,
      smartSuggestionByIdRef,
      smartSuggestionDrafts,
      studyId,
    ],
  );

  const acceptSmartSuggestion = useCallback(
    (suggestionId: string) => {
      const suggestion = smartSuggestionByIdRef.current.get(suggestionId);
      if (!suggestion) return;

      const draft = smartSuggestionDrafts[suggestionId];
      const label = (draft?.label || suggestion.label).trim();
      if (!label) {
        setSmartSuggestionError("Suggestion label is required.");
        return;
      }

      const startValueRaw = draft?.startTime || String(suggestion.startTime);
      const startTime = Number(startValueRaw);
      if (!Number.isFinite(startTime) || startTime < 0) {
        setSmartSuggestionError(
          "Suggestion start time must be a non-negative number.",
        );
        return;
      }

      const durationRaw = (draft?.durationSeconds || "").trim();
      let durationSeconds: number | null = null;
      if (durationRaw) {
        const parsedDuration = Number(durationRaw);
        if (!Number.isFinite(parsedDuration) || parsedDuration <= 0) {
          setSmartSuggestionError("Duration must be greater than 0 seconds.");
          return;
        }
        durationSeconds = parsedDuration;
      } else if (suggestion.defaultDurationSeconds > 0) {
        durationSeconds = suggestion.defaultDurationSeconds;
      }

      const notes = (draft?.notes || "").trim();
      const channels =
        suggestion.channels.length > 0
          ? suggestion.channels
          : getAnnotationChannels();

      const edited =
        label !== suggestion.label ||
        Math.abs(startTime - suggestion.startTime) > 0.001 ||
        Math.abs((durationSeconds || 0) - suggestion.defaultDurationSeconds) >
          0.001 ||
        notes.length > 0;

      const proposedAnnotation = {
        annotation_type: suggestion.annotationType,
        label,
        start_time: startTime,
        ...(durationSeconds !== null
          ? { end_time: startTime + durationSeconds }
          : {}),
        channels_affected: channels,
        notes: notes || null,
        marker_type: suggestion.markerType,
        metric: suggestion.metric,
      };

      setSmartSuggestionError(null);
      setSmartSuggestionStatus(null);
      createAnnotationMutation.mutate(
        {
          annotation_type: suggestion.annotationType,
          label,
          start_time: startTime,
          ...(durationSeconds !== null
            ? { end_time: startTime + durationSeconds }
            : {}),
          channels_affected: channels,
          color: suggestion.color,
          confidence_level: suggestion.confidence,
          ...(notes
            ? {
                notes:
                  `${notes}\n\nSuggestion rationale: ${suggestion.explanation}`.slice(
                    0,
                    5000,
                  ),
              }
            : {}),
          suggestion_decision: {
            suggestion_id: suggestion.id,
            source: suggestion.source,
            confidence: suggestion.confidence,
            explanation: suggestion.explanation,
            edited,
            proposed_annotation: proposedAnnotation,
          },
        },
        {
          onSuccess: () => {
            dismissSmartSuggestion(suggestion.id);
            setSmartSuggestionStatus(
              `Accepted suggestion as annotation at ${startTime.toFixed(1)}s.`,
            );
          },
          onError: (error) => {
            setSmartSuggestionError(toUserFacingApiError(error).message);
          },
        },
      );
    },
    [
      createAnnotationMutation,
      dismissSmartSuggestion,
      getAnnotationChannels,
      smartSuggestionByIdRef,
      smartSuggestionDrafts,
    ],
  );

  // ---- Waveform annotation CRUD handlers -------------------------------
  const handleWaveformAnnotationTimingUpdate = useCallback(
    async (
      annotationId: string,
      nextStartTime: number,
      nextEndTime?: number,
    ) => {
      if (!annotationId) return;
      setAnnotationOverlayError(null);
      await updateAnnotationMutation.mutateAsync({
        annotationId,
        updates: {
          start_time: nextStartTime,
          end_time: nextEndTime,
        },
      });
    },
    [updateAnnotationMutation],
  );

  const handleWaveformAnnotationEditNotes = useCallback(
    (marker: WaveformAnnotationMarkerItem) => {
      if (!marker.annotationId) return;
      const current = persistedAnnotationById.get(marker.annotationId);
      if (!current) return;
      openAnnotationNoteComposer({
        title: marker.label,
        placeholder: "Enter a short annotation note",
        defaultValue: current.notes || "",
        onSubmit: async (note) => {
          setAnnotationOverlayError(null);
          await updateAnnotationMutation.mutateAsync({
            annotationId: marker.annotationId as string,
            updates: { notes: note.trim() || null },
          });
        },
      });
    },
    [
      openAnnotationNoteComposer,
      persistedAnnotationById,
      updateAnnotationMutation,
    ],
  );

  const handleWaveformAnnotationAssignSelectedChannels = useCallback(
    async (marker: WaveformAnnotationMarkerItem) => {
      if (!marker.annotationId) return;
      if (selectedTraceChannels.length === 0) {
        setAnnotationOverlayError(
          "Select one or more waveform channels before assigning channels to this annotation.",
        );
        return;
      }
      setAnnotationOverlayError(null);
      await updateAnnotationMutation.mutateAsync({
        annotationId: marker.annotationId,
        updates: {
          channel_scope: "channel_set",
          channels_affected: selectedTraceChannels,
        },
      });
      toast.success("Annotation channels updated.", {
        toastId: "annotation-channels-updated",
      });
    },
    [selectedTraceChannels, updateAnnotationMutation],
  );

  const handleWaveformAnnotationAssignChannels = useCallback(
    async (marker: WaveformAnnotationMarkerItem, channels: string[]) => {
      if (!marker.annotationId) return;
      const cleanedChannels = Array.from(
        new Set(channels.map((channel) => channel.trim()).filter(Boolean)),
      );
      setAnnotationOverlayError(null);
      await updateAnnotationMutation.mutateAsync({
        annotationId: marker.annotationId,
        updates: {
          channel_scope: cleanedChannels.length > 0 ? "channel_set" : "all",
          channels_affected:
            cleanedChannels.length > 0 ? cleanedChannels : ["all"],
        },
      });
      toast.success(
        cleanedChannels.length > 0
          ? "Annotation channels updated."
          : "Annotation set to all channels.",
        {
          toastId: "annotation-channels-updated",
        },
      );
    },
    [updateAnnotationMutation],
  );

  const handleWaveformAnnotationApprove = useCallback(
    async (marker: WaveformAnnotationMarkerItem) => {
      if (!marker.annotationId) return;
      setAnnotationOverlayError(null);
      setAnnotationReviewStatus(null);
      setAnnotationReviewError(null);
      await reviewAnnotationMutation.mutateAsync(marker.annotationId);
      toast.success("Annotation approved.", {
        toastId: "annotation-approved",
      });
    },
    [reviewAnnotationMutation],
  );

  const toggleAnnotationSelection = useCallback((annotationId: string) => {
    setSelectedAnnotationIds((current) =>
      current.includes(annotationId)
        ? current.filter((id) => id !== annotationId)
        : [...current, annotationId],
    );
  }, []);

  const toggleSelectAllVisibleAnnotations = useCallback(() => {
    if (reviewFilteredAnnotationIds.length === 0) {
      setSelectedAnnotationIds([]);
      return;
    }
    setSelectedAnnotationIds((current) => {
      const currentSet = new Set(current);
      const allSelected = reviewFilteredAnnotationIds.every((id) =>
        currentSet.has(id),
      );
      if (allSelected) {
        return current.filter(
          (id) => !reviewFilteredAnnotationIds.includes(id),
        );
      }
      const merged = new Set([...current, ...reviewFilteredAnnotationIds]);
      return Array.from(merged);
    });
  }, [reviewFilteredAnnotationIds]);

  const handleBulkReviewSelectedAnnotations = useCallback(() => {
    if (selectedPendingAnnotationIds.length === 0) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(
        "Select at least one pending annotation to review.",
      );
      return;
    }
    setAnnotationBulkStatus(null);
    setAnnotationBulkError(null);
    bulkReviewAnnotationsMutation.mutate(selectedPendingAnnotationIds);
  }, [bulkReviewAnnotationsMutation, selectedPendingAnnotationIds]);

  const handleBulkDeleteSelectedAnnotations = useCallback(async () => {
    if (selectedPendingAnnotationIds.length === 0) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(
        "Select at least one non-reviewed annotation to delete.",
      );
      return;
    }
    if (
      !(await dialog.confirm(
        `Delete ${selectedPendingAnnotationIds.length} selected annotation${selectedPendingAnnotationIds.length === 1 ? "" : "s"}?`,
      ))
    ) {
      return;
    }
    setAnnotationBulkStatus(null);
    setAnnotationBulkError(null);
    bulkDeleteAnnotationsMutation.mutate(selectedPendingAnnotationIds);
  }, [bulkDeleteAnnotationsMutation, dialog, selectedPendingAnnotationIds]);

  const handleBulkTypeUpdate = useCallback(() => {
    if (selectedPendingAnnotationIds.length === 0) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(
        "Select at least one non-reviewed annotation to update.",
      );
      return;
    }
    const selectedType = annotationTypeCatalog.find(
      (entry) => entry.annotation_type_id === bulkAnnotationTypeId,
    );
    if (!selectedType) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(
        "Choose an annotation type before applying bulk type update.",
      );
      return;
    }

    setAnnotationBulkStatus(null);
    setAnnotationBulkError(null);
    bulkUpdateAnnotationsMutation.mutate({
      annotationIds: selectedPendingAnnotationIds,
      updates: {
        annotation_type_id: selectedType.annotation_type_id,
        annotation_type: selectedType.type_code,
      },
    });
  }, [
    annotationTypeCatalog,
    bulkAnnotationTypeId,
    bulkUpdateAnnotationsMutation,
    selectedPendingAnnotationIds,
  ]);

  const handleBulkChannelScopeUpdate = useCallback(() => {
    if (selectedPendingAnnotationIds.length === 0) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(
        "Select at least one non-reviewed annotation to update.",
      );
      return;
    }

    const scope = bulkChannelScopeMode === "all" ? "all" : "channel_set";
    const channels =
      scope === "all"
        ? ["all"]
        : selectedTraceChannels.length > 0
          ? selectedTraceChannels
          : [];

    if (scope === "channel_set" && channels.length === 0) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(
        "Select one or more channels first, or switch channel scope to All.",
      );
      return;
    }

    setAnnotationBulkStatus(null);
    setAnnotationBulkError(null);
    bulkUpdateAnnotationsMutation.mutate({
      annotationIds: selectedPendingAnnotationIds,
      updates: {
        channel_scope: scope,
        channels_affected: channels,
      },
    });
  }, [
    bulkChannelScopeMode,
    bulkUpdateAnnotationsMutation,
    selectedPendingAnnotationIds,
    selectedTraceChannels,
  ]);

  const handleBulkNoteAppend = useCallback(() => {
    if (selectedPendingAnnotationIds.length === 0) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError(
        "Select at least one non-reviewed annotation to update.",
      );
      return;
    }
    const noteAppend = bulkAnnotationNoteAppend.trim();
    if (!noteAppend) {
      setAnnotationBulkStatus(null);
      setAnnotationBulkError("Enter note text to append.");
      return;
    }

    setAnnotationBulkStatus(null);
    setAnnotationBulkError(null);
    bulkUpdateAnnotationsMutation.mutate(
      {
        annotationIds: selectedPendingAnnotationIds,
        updates: { note_append: noteAppend },
      },
      {
        onSuccess: () => {
          setBulkAnnotationNoteAppend("");
        },
      },
    );
  }, [
    bulkAnnotationNoteAppend,
    bulkUpdateAnnotationsMutation,
    selectedPendingAnnotationIds,
  ]);

  return {
    // panel UI state
    annotationPanelTab,
    setAnnotationPanelTab,
    annotationPanelTabIds,
    annotationOverlayError,
    setAnnotationOverlayError,
    smartSuggestionDrafts,
    setSmartSuggestionDrafts,
    smartSuggestionStatus,
    smartSuggestionError,
    annotationCategoryFilter,
    setAnnotationCategoryFilter,
    annotationAudienceStatusChip,
    handleResetAnnotationCategoryFilter,
    annotationReviewFilter,
    setAnnotationReviewFilter,
    annotationReviewStatus,
    setAnnotationReviewStatus,
    annotationReviewError,
    setAnnotationReviewError,
    selectedAnnotationIds,
    setSelectedAnnotationIds,
    annotationBulkStatus,
    annotationBulkError,
    bulkAnnotationTypeId,
    setBulkAnnotationTypeId,
    bulkChannelScopeMode,
    setBulkChannelScopeMode,
    bulkAnnotationNoteAppend,
    setBulkAnnotationNoteAppend,
    annotationNoteComposer,
    annotationNoteComposerValue,
    setAnnotationNoteComposerValue,
    annotationNoteComposerError,
    openAnnotationNoteComposer,
    closeAnnotationNoteComposer,
    submitAnnotationNoteComposer,
    selectedInterventionId,
    setSelectedInterventionId,
    responseObservedEffect,
    setResponseObservedEffect,
    responseNotes,
    setResponseNotes,
    responseSeverityDelta,
    setResponseSeverityDelta,
    responseWindowSeconds,
    setResponseWindowSeconds,
    interventionTimelineError,
    setInterventionTimelineError,
    dismissedSmartSuggestionIds,

    // queries / data
    annotationTypeCatalog,
    annotationCatalogErrored,
    annotations,
    refetchAnnotations,
    activeAnnotationsQueryKey,
    pendingReviewAnnotations,
    refetchPendingReviewAnnotations,
    interventionEvents,
    refetchInterventions,
    isFetchingInterventions,
    interventionsError,
    selectedInterventionTimeline,
    refetchInterventionTimeline,
    isFetchingInterventionTimeline,

    // derived
    annotationTypeByCode,
    resolveAnnotationCategory,
    resolveAnnotationTypeCategory,
    resolveAnnotationDisplayColor,
    categoryFilteredAnnotations,
    categoryFilteredPendingReviewAnnotations,
    renderAnnotations,
    persistedAnnotationById,
    pendingReviewIdSet,
    reviewFilteredAnnotations,
    reviewFilteredAnnotationIds,
    pendingReviewCount,
    selectedAnnotationIdSet,
    selectedAnnotations,
    selectedClipSegmentAnnotation,
    clipScissorsDisabledReason,
    selectedPendingAnnotationIds,
    allVisibleAnnotationsSelected,
    waveformAnnotationMarkers,
    selectableAnnotationTypes: annotationTypeCatalog,

    // mutations
    createAnnotationMutation,
    recordSuggestionDecisionMutation,
    updateAnnotationMutation,
    createInterventionMutation,
    createInterventionResponseMutation,
    deleteAnnotationMutation,
    reviewAnnotationMutation,
    bulkReviewAnnotationsMutation,
    bulkDeleteAnnotationsMutation,
    bulkUpdateAnnotationsMutation,
    annotationBulkActionPending,
    submitInterventionResponse,

    // handlers
    handleAnnotationPanelTabKeyDown,
    updateSmartSuggestionDraft,
    rejectSmartSuggestion,
    acceptSmartSuggestion,
    handleWaveformAnnotationTimingUpdate,
    handleWaveformAnnotationEditNotes,
    handleWaveformAnnotationAssignSelectedChannels,
    handleWaveformAnnotationAssignChannels,
    handleWaveformAnnotationApprove,
    toggleAnnotationSelection,
    toggleSelectAllVisibleAnnotations,
    handleBulkReviewSelectedAnnotations,
    handleBulkDeleteSelectedAnnotations,
    handleBulkTypeUpdate,
    handleBulkChannelScopeUpdate,
    handleBulkNoteAppend,
  };
}

export type UseEEGAnnotationPanelResult = ReturnType<
  typeof useEEGAnnotationPanel
>;
